"use client";

import "bootstrap/dist/css/bootstrap.css";
import "remixicon/fonts/remixicon.css";
import "./globals.css";

import { initializeI18n } from "@/i18n";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.css";
import "font-awesome/css/font-awesome.css";
import { Inter } from "next/font/google";
import "react-datepicker/dist/react-datepicker.css";
import "react-loading-skeleton/dist/skeleton.css";
import "react-toastify/dist/ReactToastify.css";
import "./custom-css-default.scss";
import "./custom-css.scss";
import "./fontsheet.scss";

import { FiX } from "react-icons/fi";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Bounce, Slide, ToastContainer } from "react-toastify";
import "./toast-css.scss";

const inter = Inter({ subsets: ["latin"] });

import ThriweAppWrapper, {
  ApiRepository,
  BenefitsFlow,
  InternalAxiosRequestConfig,
  MembershipsFlow,
  SessionStorageConstants,
  SessionStorageUtils,
} from "@thriwe-2-0/thriwe-ui-utils";
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "reactstrap";
import VersionChecker from "./component/VersionChecker";
import {
  ConfigProviderWrapper,
  useClientConfigProvider,
} from "./config/client-config-provider";
import ApiConstants from "./constants/api-constants";
import AppRoutes from "./constants/routes";
import StringConstants from "./constants/string-contants";
import { MixPanelProvider } from "./context/mixPanelContext";

if (ApiConstants.IS_PRODUCTION) {
  // console.log = function no_console() {};
}

const timezoneOffset = new Date();
const byPassUrlList = ["/v1/check-user"];

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const { config } = useClientConfigProvider();

  useEffect(() => {
    polyfillCountryFlagEmojis();
    require("bootstrap/dist/js/bootstrap.bundle.min.js");
  }, [config]);

  const favicon = `/client-assets/${process.env.NEXT_PUBLIC_PROJECT_CODE_ASSET}/favicon.ico`;

  return (
    <html lang="en">
      <head>
        {process.env.NODE_ENV === "production" &&
          process.env.NEXT_PUBLIC_ENV == "PRODUCTION" && (
            <>
              <script type="text/javascript" src="/js/newrelic.js" async />
              <script
                async
                src="https://www.googletagmanager.com/gtag/js?id=G-11GK1VW0G6"
              />
              <script
                dangerouslySetInnerHTML={{
                  __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag() {
                    dataLayer.push(arguments);
                  }
                  gtag('js', new Date());
                  gtag('config', 'G-11GK1VW0G6');
                  gtag('config', 'G-5P91CQ9NGV');
                  ${
                    process.env.NEXT_PUBLIC_PROJECT_CODE == "MASTERCARD_CL"
                      ? "gtag('config', 'G-W0H09RVV2L');"
                      : ""
                  }
                  ${
                    process.env.NEXT_PUBLIC_PROJECT_CODE == "MASTERCARD_CL"
                      ? "gtag('config', 'DC-12016036');"
                      : ""
                  }
                `,
                }}
              />

              {process.env.NEXT_PUBLIC_PROJECT_CODE == "MASTERCARD_CL" && (
                <script
                  type="text/javascript"
                  src="/js/contactless-meta.js"
                  async
                />
              )}
            </>
          )}
        <link
          rel="icon"
          href={favicon}
          type="image/x-icon"
          sizes="128x128"
        ></link>
      </head>
      <body className={inter.className}>
        <ToastContainer
          transition={config?.designVersion === "v1" ? Slide : Bounce}
          limit={3}
          autoClose={2000}
          closeButton={
            <span
              className={`toast-close-icon-container${
                config?.designVersion == "v1" ? "" : "-v2"
              }`}
            >
              <FiX />
            </span>
          }
        />
        <VersionChecker />
        <ConfigProviderWrapper>
          <ConfigWrapper>{children}</ConfigWrapper>
        </ConfigProviderWrapper>
      </body>
    </html>
  );
}

const ConfigWrapper = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);

  const { config } = useClientConfigProvider();
  useEffect(() => {
    initializeI18n().then(() => setIsI18nInitialized(true));
    let savedLang = SessionStorageUtils.getFromSessionStorage(
      SessionStorageConstants.LANGUAGE
    );
  }, []);

  const apiRepository = useMemo(() => {
    if (!config?.nativeDataSavingType) return null;
    return new ApiRepository({
      userApiEndpoint: ApiConstants.THRIWE_UMS_API_URL,
      discoveryApiEndpoint: ApiConstants.THRIWE_DISCOVERY_API_URL,
      redemptionApiEndpoint: ApiConstants.THRIWE_RMS_API_URL,
      feedbackApiEndpoint: ApiConstants.THRIWE_FEEDBACK_API_URL,
      projectId: ApiConstants.PROJECT_ID,
      projectIdHeaderKey: "Project-Code",
      nativeDataSavingType: config.nativeDataSavingType,

      requestInterceptor: (config?: InternalAxiosRequestConfig) => {
        console.log("request interceptor config", config?.url);

        if (config) {
          config.headers[ApiConstants.X_TIMEZONE_OFFSET] =
            timezoneOffset.getTimezoneOffset();
          config.headers[ApiConstants.X_TIMEZONE] =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
          config.headers[ApiConstants.X_LANGUAGE] =
            SessionStorageUtils.getFromSessionStorage(
              SessionStorageConstants.LANGUAGE
            );

          if (!byPassUrlList.includes(config.url || "")) {
            const userRegion = SessionStorageUtils.getFromSessionStorage(
              StringConstants.PARAM_X_REGION
            );

            if (userRegion) {
              config.headers[ApiConstants.X_USER_REGION] = userRegion;
            }
          }
        }
      },

      errorResponseInterceptor: (error) => {
        console.log("testinggg", error?.response?.data?.statusCode);

        if (error?.response?.data?.statusCode == 401) {
          showSessionExpiredModal();
        }
      },
    });
  }, [config?.nativeDataSavingType]);

  if (!apiRepository || !isI18nInitialized)
    return (
      <div className="inital-loading-screen">
        <Spinner />
      </div>
    );

  return (
    <ThriweAppWrapper
      repository={apiRepository}
      benefitsFlow={BenefitsFlow.MEMBERSHIP_SPECIFIC_BENEFITS}
      autoFetchProjectUtilities={true}
      membershipFlow={config?.membershipFlow ?? MembershipsFlow?.DEFAULT}
      enableBookmarks={config?.enableBookmarks ?? false}
      nativeDataSavingType={config!.nativeDataSavingType!}
    >
      <MixPanelProvider>{children}</MixPanelProvider>
    </ThriweAppWrapper>
  );
};

function showSessionExpiredModal() {
  if (document.getElementById("session-expired-modal")) return;

  const modal = document.createElement("div");
  modal.id = "session-expired-modal";
  modal.innerHTML = `
    <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
      <div style="background: white; padding: 30px; border-radius: 24px; text-align: center; max-width: 90%; width: 400px;">
        <h2 style="font-size: 28px; margin-bottom: 10px; color: var(--color-grayscale-text-title);">Session Expired</h2>
        <p style="margin-bottom: 25px;font-size: 14px; color:var(--color-grayscale-text-subtitle)">Your session has expired. Please log in again.</p>
        <button id="session-expired-ok" style="padding: 10px 30px; background-color:var(--surface-default-primary); color: var(--color-grayscale-text-negative); border: none; border-radius: 5px; cursor: pointer; font-size: 16px;
          font-weight: 600; border-radius: 20px;">
          Okay
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  document
    .getElementById("session-expired-ok")!
    .addEventListener("click", () => {
      sessionStorage.clear();
      localStorage.clear();
      window.open(AppRoutes.login, "_self");
    });
}
